import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';
import { CheckCircle, AlertCircle, Zap, Server, Database, Shield, GitBranch, Cpu, Cloud, Lock, Boxes, Activity } from 'lucide-react';

const Experience = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, threshold: 0.1 });

  const experienceCategories = [
    {
      title: "Backend, API & System Architecture",
      icon: Server,
      problems: [
        {
          faced: "APIs slowing under concurrent traffic.",
          solved: "Implemented async workers, optimized database indexes, and added Redis caching to reduce load by 60%.",
        },
        {
          faced: "Repetitive logic and messy controllers in backend.",
          solved: "Refactored services into modular Go packages with clean architecture & reusable middlewares.",
        },
        {
          faced: "Heavy JSON payloads causing timeouts.",
          solved: "Introduced pagination, selective fields, and split endpoints → boosting API reliability.",
        }
      ]
    },
    {
      title: "Database & Query Optimization",
      icon: Database,
      problems: [
        {
          faced: "Slow SQL queries scanning full tables.",
          solved: "Added composite indexes, optimized WHERE conditions, and rewrote joins → 4x faster queries.",
        },
        {
          faced: "MySQL bottlenecks during peak usage.",
          solved: "Tuned max connections, reduced idle sleeps, added connection pooling & shorter TTL.",
        },
        {
          faced: "Data duplication & inconsistent records.",
          solved: "Enforced constraints, unique keys, and introduced cleanup scripts & validation checks.",
        }
      ]
    },
    {
      title: "Server Management & DevOps",
      icon: Cloud,
      problems: [
        {
          faced: "Slow manual server setup for every new deployment.",
          solved: "Created Proxmox VM templates with Cloud-init → one-click server provisioning.",
        },
        {
          faced: "Complex multi-domain routing & SSL setup.",
          solved: "Automated Nginx reverse proxy + Cloudflare SSL with Dockerized configs.",
        },
        {
          faced: "Services randomly crashing without logs.",
          solved: "Configured systemd, health checks, log rotation, and uptime monitoring.",
        },
        {
          faced: "Need for secure internal communication between services.",
          solved: "Configured private bridging networks, firewall rules & IP whitelisting.",
        }
      ]
    },
    {
      title: "Deployment & CI/CD Automation",
      icon: GitBranch,
      problems: [
        {
          faced: "Manual deployments causing downtime.",
          solved: "Built CI/CD scripts with Docker Compose + webhooks for instant zero-downtime updates.",
        },
        {
          faced: "Developers syncing .env files manually and breaking builds.",
          solved: "Centralized environment management with versioned templates & secrets.",
        },
        {
          faced: "Containers failing on updates.",
          solved: "Added rollback logic, pre-deployment checks & staging mode.",
        }
      ]
    },
    {
      title: "Frontend & Mobile Application",
      icon: Cpu,
      problems: [
        {
          faced: "React Native app lag and slow list rendering.",
          solved: "Implemented virtualization, memoized components & reduced re-renders.",
        },
        {
          faced: "Dependencies causing inconsistent builds.",
          solved: "Locked package versions, cleaned cache layers & added build scripts.",
        },
        {
          faced: "Offline-first data inconsistencies.",
          solved: "Designed sync logic with timestamp diff, local DB migrations & conflict resolution.",
        }
      ]
    },
    {
      title: "Security & Authentication",
      icon: Shield,
      problems: [
        {
          faced: "Risk of unauthorized API access.",
          solved: "Added JWT rotation, IP checks, rate limiting & audit logs.",
        },
        {
          faced: "Login failures due to OAuth callback issues.",
          solved: "Standardized Google/Azure OAuth flows & handled token refresh failures.",
        },
        {
          faced: "Users misusing API endpoints.",
          solved: "Implemented RBAC, scoped tokens & per-role permissions.",
        }
      ]
    },
    {
      title: "Infrastructure, Backup & Recovery",
      icon: Boxes,
      problems: [
        {
          faced: "Risk of losing data during server crashes.",
          solved: "Automated MySQL backups, VM snapshots, and Docker volume replication.",
        },
        {
          faced: "No quick restoration mechanism during outages.",
          solved: "Built disaster-recovery scripts with one-command restore.",
        },
        {
          faced: "Proxmox storage filling up.",
          solved: "Implemented remote backup storage, auto cleanup & rotation policies.",
        }
      ]
    },
    {
      title: "Monitoring & Reliability",
      icon: Activity,
      problems: [
        {
          faced: "Hard to track crashes or performance issues.",
          solved: "Integrated monitoring tools, log analyzers, and alert systems.",
        },
        {
          faced: "High CPU usage during peak operations.",
          solved: "Optimized goroutines, DB connection pooling, and load balancing.",
        }
      ]
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        duration: 0.6,
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut"
      }
    }
  };

  return (
    <section id="experience" className="experience section">
      <div className="container">
        <motion.div
          ref={ref}
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
        >
          <motion.h2 
            className="section-title"
            variants={itemVariants}
          >
            💼 Experience & Problem Solving
          </motion.h2>

          <motion.div 
            className="experience-intro"
            variants={itemVariants}
          >
            <p>
              Real-world challenges I've tackled and the solutions that made an impact.
            </p>
          </motion.div>

          {/* Experience Categories */}
          {experienceCategories.map((category, categoryIndex) => {
            const IconComponent = category.icon;
            return (
              <motion.div
                key={categoryIndex}
                className="experience-category"
                variants={itemVariants}
              >
                <div className="category-header">
                  <div className="category-icon">
                    <IconComponent size={28} />
                  </div>
                  <h3 className="category-title">{category.title}</h3>
                </div>
                
                <div className="problem-solutions-list">
                  {category.problems.map((item, index) => (
                    <motion.div
                      key={index}
                      className="problem-solution-row"
                      variants={itemVariants}
                      whileHover={{ 
                        x: 5,
                        backgroundColor: "rgba(125, 83, 246, 0.03)"
                      }}
                      transition={{ duration: 0.2 }}
                    >
                      <div className="problem-row">
                        <div className="problem-label">
                          <AlertCircle size={18} className="problem-indicator" />
                          <span>Faced:</span>
                        </div>
                        <p className="problem-text">{item.faced}</p>
                      </div>
                      
                      <div className="solution-row">
                        <div className="solution-label">
                          <CheckCircle size={18} className="solution-indicator" />
                          <span>Solved:</span>
                        </div>
                        <p className="solution-text">{item.solved}</p>
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default Experience;
